const API_URL = import.meta.env.VITE_API_URL || 'https://api.jumpstoneauth.com'

interface TokenResponse {
  access: string
  refresh: string
}

class ApiService {
  private accessToken: string | null = null
  private refreshToken: string | null = null

  constructor() {
    // Load tokens from localStorage on init
    this.accessToken = localStorage.getItem('access_token')
    this.refreshToken = localStorage.getItem('refresh_token')
  }

  hasTokens(): boolean {
    return !!this.accessToken && !!this.refreshToken
  }

  async obtainToken(email: string, password: string): Promise<TokenResponse> {
    const response = await fetch(`${API_URL}/api/token/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Login failed' }))
      throw new Error(error.detail || 'Invalid credentials')
    }

    const data: TokenResponse = await response.json()
    this.setTokens(data)
    return data
  }

  async register(email: string, password: string, fullName: string): Promise<void> {
    const response = await fetch(`${API_URL}/api/register/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password, full_name: fullName }),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Registration failed' }))
      throw new Error(error.detail || 'Could not create account')
    }

    // Auto-login after registration
    await this.obtainToken(email, password)
  }

  async verifyToken(): Promise<boolean> {
    if (!this.accessToken) return false

    try {
      const response = await fetch(`${API_URL}/api/token/verify/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: this.accessToken }),
      })

      if (response.ok) return true

      // Try to refresh if verification fails
      return await this.refreshAccessToken()
    } catch {
      return false
    }
  }

  async refreshAccessToken(): Promise<boolean> {
    if (!this.refreshToken) return false

    try {
      const response = await fetch(`${API_URL}/api/token/refresh/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh: this.refreshToken }),
      })

      if (!response.ok) {
        this.clearTokens()
        return false
      }

      const data = await response.json()
      this.accessToken = data.access
      localStorage.setItem('access_token', data.access)
      return true
    } catch {
      this.clearTokens()
      return false
    }
  }

  private setTokens(tokens: TokenResponse): void {
    this.accessToken = tokens.access
    this.refreshToken = tokens.refresh
    localStorage.setItem('access_token', tokens.access)
    localStorage.setItem('refresh_token', tokens.refresh)
  }

  clearTokens(): void {
    this.accessToken = null
    this.refreshToken = null
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
  }

  async fetch(endpoint: string, options: RequestInit = {}): Promise<Response> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    }

    if (this.accessToken) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.accessToken}`
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
    })

    // If unauthorized, try to refresh token
    if (response.status === 401 && this.refreshToken) {
      const refreshed = await this.refreshAccessToken()
      if (refreshed) {
        // Retry the request with new token
        (headers as Record<string, string>)['Authorization'] = `Bearer ${this.accessToken}`
        return fetch(`${API_URL}${endpoint}`, { ...options, headers })
      }
    }

    return response
  }
}

export const api = new ApiService()

